<template>
  <div class="verify-wrapper">
    <header class="verify-header">
      <h1>Verificar Pago</h1>
      <router-link to="/welcome" class="back-button">Volver al Dashboard</router-link>
    </header>
    <main class="verify-content">
      <div class="verify-form-container">
        <form @submit.prevent="submitVerification">
          <div class="input-group">
            <label for="name">Nombre del Cliente</label>
            <input type="text" id="name" v-model="name" placeholder="Ej: John Doe" required>
          </div>
          <div class="input-group">
            <label for="amount">Monto</label>
            <input type="text" id="amount" v-model="formattedAmount" @input="formatAmount" placeholder="0.00" required>
          </div>
          <button type="submit" class="verify-button" :disabled="isLoading">
            {{ isLoading ? 'Buscando...' : 'Verificar' }}
          </button>
        </form>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>
      </div>

      <div class="results-container" v-if="searched">
        <h2>Resultados de la Búsqueda</h2>
        <div v-if="isLoading" class="loader"></div>
        <div v-else-if="searchResults.length > 0">
          <ul>
            <li v-for="(payment, index) in searchResults" :key="payment._id" class="result-item">
              <div class="payment-info">
                <p><strong>Nombre:</strong> {{ payment.nombre }}</p>
                <p><strong>Monto:</strong> ${{ payment.monto.toFixed(2) }}</p>
                                <p><strong>Fecha:</strong> {{ formatDateTime(payment.fecha) }}</p>
                <p><strong>Estado:</strong> <span :class="`status-${payment.estatus.toLowerCase()}`">{{ payment.estatus }}</span></p>
              </div>
              <div class="payment-action">
                <input 
                  type="file" 
                  accept="image/*" 
                  @change="handleFileUpload($event, payment._id, index)" 
                  :ref="`fileInput_${payment._id}`" 
                  style="display: none;" 
                />
                <button 
                  v-if="payment.estatus.toLowerCase() === 'pendiente'"
                  @click="triggerFileInput(payment._id)"
                  :disabled="isRegistering(payment._id)"
                  class="register-button">
                  <span v-if="isRegistering(payment._id)">Registrando...</span>
                  <span v-else>Registrar</span>
                </button>
                <div v-if="payment.estatus.toLowerCase() === 'asignado'" class="registered-note">
                  <p>Este Pago ya está registrado a tu cuenta.</p>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else-if="!error" class="no-results">
          <p>No se encontraron resultados.</p>
        </div>
      </div>

      <div class="zelle-history-container" v-if="zellePayments.length > 0">
        <h2>Historial de Pagos Zelle</h2>
        <div class="zelle-summary">
          <div class="summary-item">
            <p><strong>Saldo a Favor:</strong></p>
            <span>${{ assignedZelleTotal.toFixed(2) }}</span>
          </div>
          <div class="summary-item">
            <p><strong>Pagos Asignados:</strong></p>
            <span>{{ assignedZelleCount }}</span>
          </div>
        </div>
        <ul class="zelle-list">
          <li v-for="payment in zellePayments" :key="payment._id" class="zelle-item">
            <p><strong>Monto:</strong> ${{ payment.monto.toFixed(2) }}</p>
                        <p><strong>Fecha:</strong> {{ formatDateTime(payment.fecha) }}</p>
            <p><strong>Estado:</strong> <span :class="`status-${payment.estatus.toLowerCase()}`">{{ payment.estatus }}</span></p>
          </li>
        </ul>
      </div>
    </main>

    <div v-if="registrationSuccess" class="success-modal">
      <div class="success-modal-content">
        <p>¡Pago registrado exitosamente!</p>
        <p>¿Qué deseas hacer ahora?</p>
        <div class="success-modal-actions">
          <button @click="resetForNewVerification" class="modal-button">Validar Otro Pago</button>
          <router-link to="/welcome" class="modal-button">Ir al Inicio</router-link>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VerifyComponent',
  data() {
    return {
      name: '',
      amount: 0,
      formattedAmount: '',
      searchResults: [],
      isLoading: false,
      error: null,
      searched: false, // Para saber si ya se realizó una búsqueda
      registeringIds: [], // Array to track which payments are being registered
      registrationSuccess: false,
      zellePayments: [] // Initialize zellePayments
    };
  },
  computed: {
    assignedZelleCount() {
      if (!this.zellePayments) return 0;
      return this.zellePayments.filter(p => p.estatus === 'Asignado').length;
    },
    assignedZelleTotal() {
      if (!this.zellePayments) return 0;
      return this.zellePayments
        .filter(p => p.estatus === 'Asignado')
        .reduce((total, p) => total + p.monto, 0);
    }
  },
  methods: {
    formatDateTime(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      return date.toLocaleString('es-VE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    formatAmount(event) {
      let value = event.target.value.replace(/[^\d]/g, '');
      if (!value) {
        this.formattedAmount = '';
        this.amount = 0;
        return;
      }

      while (value.length < 3) {
        value = '0' + value;
      }

      let integerPart = value.slice(0, -2);
      let decimalPart = value.slice(-2);

      this.amount = parseFloat(`${integerPart}.${decimalPart}`);
      
      integerPart = parseInt(integerPart, 10).toLocaleString('en-US');

      this.formattedAmount = `${integerPart}.${decimalPart}`;
    },
    async submitVerification() {
      if (!this.name || this.amount <= 0) {
        this.error = 'Por favor, completa todos los campos correctamente.';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.searchResults = [];
      this.searched = true;

      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        const response = await axios.post(`${apiUrl}/zelle/search`, {
          name: this.name,
          amount: this.amount
        });
        this.searchResults = response.data;

        // If there are results, check if any are assigned and fetch the client's history
        if (this.searchResults.length > 0) {
          const assignedPayment = this.searchResults.find(p => p.estatus === 'Asignado' && p.cliente);
          if (assignedPayment) {
            this.fetchZellePayments(assignedPayment.cliente);
          } else {
            // If no payment is assigned, clear any previous history
            this.zellePayments = [];
          }
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.error = 'No se encontraron pagos que coincidan con la búsqueda.';
        } else {
          this.error = 'Ocurrió un error al realizar la búsqueda. Por favor, intenta de nuevo.';
        }
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    triggerFileInput(paymentId) {
      this.$refs[`fileInput_${paymentId}`][0].click();
    },

    isRegistering(paymentId) {
      return this.registeringIds.includes(paymentId);
    },

    async handleFileUpload(event, paymentId, index) {
      const file = event.target.files[0];
      if (!file) return;

      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.co_cli) {
        this.error = 'No se pudo obtener la información del usuario para registrar el pago.';
        return;
      }

      this.registeringIds.push(paymentId);

      const formData = new FormData();
      formData.append('comprobante', file);
      formData.append('clientId', user.co_cli);
      formData.append('clientName', user.nombre);

      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        const response = await axios.post(`${apiUrl}/zelle/assign/${paymentId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.searchResults[index] = response.data;
        this.registrationSuccess = true; // Mostrar el modal de éxito
        this.fetchZellePayments(user.co_cli);

      } catch (err) {
        this.error = 'Ocurrió un error al registrar el pago.';
        console.error(err);
      } finally {
        this.registeringIds = this.registeringIds.filter(id => id !== paymentId);
      }
    },
    async fetchZellePayments(clientId) {
      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        const response = await axios.get(`${apiUrl}/zelle/cliente/${clientId}`);
        this.zellePayments = response.data;
      } catch (err) {
        console.error('Error fetching zelle payments:', err);
        // Optionally show an error to the user
      }
    },
    resetForNewVerification() {
      this.name = '';
      this.amount = 0;
      this.formattedAmount = '';
      this.searchResults = [];
      this.isLoading = false;
      this.error = null;
      this.searched = false;
      this.registrationSuccess = false;
      this.zellePayments = [];
    }
  }
};
</script>

<style scoped>
.verify-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f0f2f5;
}

.verify-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.verify-header h1 {
  font-size: 24px;
  color: #333;
  margin: 0;
}

.back-button {
  padding: 8px 16px;
  font-size: 14px;
  color: #fff;
  background-color: #42b983;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s;
}

.back-button:hover {
  background-color: #369f72;
}

.verify-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 30px;
  gap: 40px;
}

.verify-form-container {
  width: 100%;
  max-width: 500px;
  padding: 40px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.input-group {
  margin-bottom: 25px;
}

.input-group label {
  display: block;
  font-size: 16px;
  color: #333;
  margin-bottom: 8px;
}

input {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-sizing: border-box;
  transition: border-color 0.3s;
}

input:focus {
  outline: none;
  border-color: #42b983;
}

.verify-button {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background-color: #42b983;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.verify-button:hover {
  background-color: #369f72;
}

.error-message {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
  text-align: center;
}

.results-container {
  width: 100%;
  max-width: 500px;
  padding: 30px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.results-container h2 {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
}

.loader {
  margin: 20px auto;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.result-item {
  list-style: none;
  padding: 15px;
  border: 1px solid #eee;
  border-radius: 5px;
  margin-bottom: 15px;
  background-color: #fafafa;
}

.result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-item p {
  margin: 5px 0;
  font-size: 16px;
}

.register-button {
  padding: 8px 16px;
  font-size: 14px;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.register-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.register-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.status-pendiente {
  color: #ffc107;
  font-weight: bold;
}

.status-asignado {
  color: #28a745;
  font-weight: bold;
}

.registered-note p {
  margin: 0;
  font-size: 14px;
  color: #28a745;
  font-style: italic;
}

.no-results {
  text-align: center;
  color: #666;
  font-size: 18px;
  padding: 20px;
}

ul {
  padding: 0;
}

.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.success-modal-content {
  background-color: #fff;
  padding: 30px 40px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  text-align: center;
  max-width: 400px;
}

.success-modal-content p {
  font-size: 18px;
  color: #333;
  margin-bottom: 20px;
}

.success-modal-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 25px;
}

.modal-button {
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
  text-decoration: none;
  color: #fff;
  background-color: #42b983;
}

.modal-button:hover {
  background-color: #369f72;
}

.modal-button:last-child {
  background-color: #007bff;
}

.modal-button:last-child:hover {
  background-color: #0056b3;
}

.zelle-history-container {
  width: 100%;
  max-width: 500px;
  margin-top: 20px;
  padding: 30px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.zelle-history-container h2 {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
}

.zelle-summary {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.summary-item {
  text-align: center;
}

.summary-item p {
  margin: 0;
  font-size: 16px;
  color: #555;
}

.summary-item span {
  font-size: 20px;
  font-weight: bold;
  color: #42b983;
}

.zelle-list {
  list-style: none;
  padding: 0;
}

.zelle-item {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.zelle-item:last-child {
  border-bottom: none;
}

.zelle-item p {
  margin: 0;
  font-size: 16px;
}
</style>
